import { Signal, signal } from '@angular/core';
import { compareObjects } from '../shared/funcs/helpers';
import { CurrencyType, getCurrencyFormatByPrimary } from '../shared/currency/currency-format';

export class Lot {
  details!: LotDetail;
  private lotInf: any;
  private lotBids: Bid[] = [];
  private cfg = signal<any>({});
  private bidAvailable!: boolean;
  private prevLotCode!: string;
  private currentBidValue!: number;

  get config(): Signal<any> {
    return this.cfg;
  }

  set config(cfg: any) {
    this.cfg.set(cfg);
  }

  set bids(lotBids: any) {
    this.lotBids = [];
    const bidKeys = Object.keys(lotBids).filter((k) => k !== 'highest');
    for (const bidKey of bidKeys.sort((a, b) => +b - +a)) {
      this.lotBids.push({
        user: lotBids[bidKey].usuario,
        value: lotBids[bidKey].valor,
        date: lotBids[bidKey].fecha,
        winner: false,
      });
    }
  }

  get bids() {
    return this.lotBids;
  }

  set lotInfo(lotInf: any) {
    this.lotInf = lotInf;
    this.currentBidValue = +this.lotInf.sysPrice;
  }

  get lotInfo() {
    return this.lotInf;
  }

  get isBidAvailable() {
    return (this.cfg() && this.cfg().active && this.cfg().actualLot) || false;
  }

  get currentLotId() {
    return this.cfg().actualLot;
  }

  get videoUrl() {
    return this.lotInf.video;
  }

  get currBidValue() {
    return this.isBidAvailable || this.bids.length > 0
      ? this.currentBidValue
      : this.lotInf.sysPrice;
  }

  get sysPrice() {
    return this.lotInf.sysPrice;
  }

  isBidOutdated(nextBidValue: number) {
    return +this.lotInf.sysPrice >= nextBidValue;
  }

  isActive() {
    return this.isBidAvailable || this.bids.length === 0;
  }

  isFinished() {
    return !this.isBidAvailable && this.bids.length > 0;
  }

  isPaused() {
    return this.cfg().paused === true;
  }

  isEditing() {
    return this.cfg().editing === true;
  }

  isCutting() {
    return this.cfg().cutting === true;
  }

  firstBidValue() {
    return +this.lotInf.sysPrice + +this.lotInf.increment;
  }

  nextBidValue() {
    this.currentBidValue = this.currentBidValue + +this.lotInf.increment;
    return this.currentBidValue;
  }

  hasLastBid(username: string) {
    return this.bids && this.bids.length > 0 && this.bids[0].user === username;
  }

  trackPreviousLot() {
    this.prevLotCode = this.cfg().actualLot;
  }

  shouldLoadNext() {
    return this.cfg().actualLot && this.prevLotCode !== this.cfg().actualLot;
  }

  checkWinner() {
    if (!this.lotBids || this.lotBids.length <= 0) {
      return;
    }

    this.lotBids[0].winner = this.isBidAvailable ? false : true;
  }

  isLotInfoStale(newInfo: any) {
    if (!this.lotInf) {
      return true;
    }

    const currLotInfo = JSON.parse(JSON.stringify(this.lotInf));
    const newLotInfo = JSON.parse(JSON.stringify(newInfo));

    delete currLotInfo['state'];
    delete newLotInfo['state'];

    return !compareObjects(currLotInfo, newLotInfo);
  }

  calculateValues(): LotDetail {
    const detail = {
      type: this.lotInf[LotNames.TYPE],
      quantity: +this.lotInf[LotNames.QUANTITY],
      arrivedTime: this.lotInf[LotNames.ARRIVED_TIME],
      observations: this.lotInf[LotNames.OBSERVATIONS],
      outOrder: this.lotInf[LotNames.OUT_ORDER],
      weight: +this.lotInf[LotNames.WEIGHT],
      origin: this.lotInf[LotNames.ORIGIN],
      sex: this.lotInf[LotNames.CATEGORY],
      bid: this.lotInf.increment,
      avgWeight:
        +this.lotInf[LotNames.WEIGHT] / +this.lotInf[LotNames.QUANTITY],
      basePrice: +this.lotInf[LotNames.BASE_PRICE],
      kiloPrice: 0,
      finalPrice: +this.lotInf.increment + +this.lotInf.sysPrice,
      animalPrice: 0,
      totalPrice: 0,
    };

    detail.kiloPrice = this.getKiloPrice(
      detail,
      +this.lotInf.increment,
      +this.lotInf.sysPrice
    );
    detail.animalPrice = this.getAnimalPrice(
      detail,
      +this.lotInf.increment,
      +this.lotInf.sysPrice
    );
    detail.totalPrice = this.getTotalPrice(detail);

    this.details = detail;

    return detail;
  }

  static detailDefault(): LotDetail {
    return {
      quantity: 0,
      arrivedTime: '',
      observations: '',
      outOrder: '',
      weight: 0,
      avgWeight: 0,
      bid: 0,
      basePrice: 0,
      animalPrice: 0,
      finalPrice: 0,
      kiloPrice: 0,
      totalPrice: 0,
      origin: '',
      sex: '',
      type: '',
    };
  }

  reset() {
    this.details = undefined as any;
    this.prevLotCode = '';
    this.currentBidValue = 0;
    this.lotBids = [];
  }

  /**
   * Verifica si hay un lote configurado que no sea un lote anticipado
   * @returns true si hay lote configurado y no es anticipado
   */
  hasConfiguredLot(): boolean {
    const config = this.cfg();
    return config.actualLot && 
           config.actualLot !== '-' && 
           config.anticipated !== true;
  }

  /**
   * Verifica si hay un lote en pista
   * @returns true si hay lote en pista
   */
  hasActiveLiveAuctionLot(): boolean {
    const config = this.cfg();
    return !!(
      config.actualLot &&
      config.actualLot !== '-' &&
      config.anticipated !== true &&
      config.active === true
    );
  }

  /**
   * Establece el ID del lote actual y configura el estado para pujas anticipadas
   * @param lotId ID del lote a establecer
   */
  setCurrentLotId(lotId: string): void {
    if (!lotId || lotId.trim() === '') {
      console.warn('Lot: lotId is required and cannot be empty');
      return;
    }

    const currentConfig = this.cfg();
    this.cfg.set({
      ...currentConfig,
      actualLot: lotId,
      active: true,
      paused: false,
      editing: false,
      cutting: false,
      anticipated: true
    });
  }

  private getAnimalPrice(
    detail: LotDetail,
    increment: number,
    sysPrice: number
  ) {
    switch (detail.type) {
      case LotType.ON_SALE:
        return increment + sysPrice;
      case LotType.ON_AUCTION:
        return (detail.weight / detail.quantity) * detail.kiloPrice;
      default:
        return 0;
    }
  }

  private getKiloPrice(detail: LotDetail, increment: number, sysPrice: number) {
    switch (detail.type) {
      case LotType.ON_SALE:
        return detail.finalPrice / detail.avgWeight;
      case LotType.ON_AUCTION:
        return increment + sysPrice;
      default:
        return 0;
    }
  }

  private getTotalPrice(detail: LotDetail) {
    switch (detail.type) {
      case LotType.ON_SALE:
        return detail.finalPrice * detail.quantity;
      case LotType.ON_AUCTION:
        return detail.weight * detail.kiloPrice;
      default:
        return 0;
    }
  }
}

export class AuctionExecution {
  private isRunning: boolean;
  private startedAt: Date;
  private finishedAt: Date;

  constructor(isRunning: boolean, startedAt: any, finishedAt: any) {
    this.isRunning = isRunning;
    this.startedAt = new Date(startedAt * 1000);
    this.finishedAt = new Date(finishedAt * 1000);
  }

  get isOpen() {
    return this.isRunning;
  }
}

export interface LotDetail {
  quantity: number;
  arrivedTime: string;
  observations: string;
  outOrder: string;
  weight: number;
  avgWeight: number;
  bid: number;
  basePrice: number;
  animalPrice: number;
  finalPrice: number;
  kiloPrice: number;
  totalPrice: number;
  origin: string;
  sex: string;
  type: string;
}

export interface Bid {
  date: string;
  user: string;
  value: number;
  winner: boolean;
}

export enum LotType {
  ON_SALE = 'remate',
  ON_AUCTION = 'subasta',
}

export enum LotNames {
  TYPE = 'type',
  ORIGIN = 'origin',
  WEIGHT = 'weight',
  OUT_ORDER = 'orden sal',
  OBSERVATIONS = 'observations',
  ARRIVED_TIME = 'arrivedTime',
  CATEGORY = 'category',
  QUANTITY = 'quantity',
  BASE_PRICE = 'basePrice',
}

export enum UserType {
  VIRTUAL = 'Virtual',
  ONSITE = 'Presencial',
}

export interface UserConfig {
  readonly confirmaPuja: boolean;
  readonly isAssisted: boolean;
}

export interface LotCurrencyInfo {
  currencyCode: string;
  currencySymbol: string;
  currencyFormat: string;
  exchangeRate: number;
}

export class LotCurrency {
  private firstCurrencyExchangeRate: number;
  private secondCurrencyExchangeRate: number;
  private primaryCurrency: string;

  constructor(primaryCurrency: string,  exchangeRates: number[]) {
    this.firstCurrencyExchangeRate = exchangeRates[0];
    this.secondCurrencyExchangeRate = exchangeRates[1];
    this.primaryCurrency = primaryCurrency;
  }

  get firstCurrency() {
    return {
      currencyCode: this.primaryCurrency,
      currencySymbol: getCurrencyFormatByPrimary(this.primaryCurrency).currencySymbol,
      currencyFormat: getCurrencyFormatByPrimary(this.primaryCurrency).currencyFormat,
      exchangeRate: this.firstCurrencyExchangeRate,
    };
  }

  get secondCurrency() {
    if (!this.secondCurrencyExchangeRate) {
      return undefined;
    }

    return {
      currencyCode: CurrencyType.USD,
      currencySymbol: getCurrencyFormatByPrimary(CurrencyType.USD).currencySymbol,
      currencyFormat: getCurrencyFormatByPrimary(CurrencyType.USD).currencyFormat,
      exchangeRate: this.secondCurrencyExchangeRate,
    };
  }
}
